import React from 'react';
import { Button, Typography } from '@milanlaser/mlcomponents';
import { renderTextWithHighlight } from '../../utils/general/textFormatting';
import { getActiveLpPromoBGColor, getCurrentVariantConfig, PromoVariant } from '../../utils/lpPromoConfig';
import clsx from 'clsx';

interface LandingPageHeroProps {
    badge?: string;
    eyebrow?: string;
    subheading1?: string;
    heading?: string;
    subheading2?: string;
    highlightWord?: string;
    buttonText?: string;
    buttonLink?: string;
    imageAlt?: string;
    heroImageMobile?: string;
    heroImageDesktop?: string;
    mobileHeadingBreak?: boolean;
    variant?: PromoVariant;
    bgColor?: string;
    /** Render subheading2 above subheading1 instead of below the heading. */
    subheading2First?: boolean;
}

export function LandingPageHero({
    eyebrow,
    heading,
    subheading1,
    subheading2,
    highlightWord,
    buttonText,
    buttonLink,
    imageAlt,
    heroImageMobile,
    heroImageDesktop,
    mobileHeadingBreak = true,
    variant,
    bgColor: bgColorProp,
    subheading2First = false,
}: LandingPageHeroProps) {
    // Get dynamic images based on current variant if not provided
    const variantConfig = getCurrentVariantConfig();
    const mobileImage = heroImageMobile || variantConfig.images.mobile;
    const desktopImage = heroImageDesktop || variantConfig.images.desktop;
    const bgColor = bgColorProp || getActiveLpPromoBGColor();
    const isEvergreen = (variant || variantConfig.variant) === 'evergreen';

    const handleButtonClick = () => {
        if (buttonLink) {
            window.location.href = buttonLink;
        }
    };

    const isHexColor = bgColor.startsWith('#');

    // Each line keeps the type treatment it has today; only the order changes. The
    // vertical rhythm tracks position rather than the element, so whichever line
    // closes the copy block owns the gap above the CTA.
    const subheading1Block = subheading1 && (
        <Typography
            variant="h2-small"
            tag="p"
            color={isEvergreen ? 'Black' : 'Navy'}
            className={clsx('!normal-case !text-[1.5rem] md:!text-[2rem] !font-normal', {
                'mb-8': isEvergreen,
            })}
        >
            {subheading1}
        </Typography>
    );

    const headingBlock = heading && (
        <Typography
            variant="h1"
            tag="h1"
            color={isEvergreen ? 'Black' : 'Navy'}
            // `mb-8` needs the important modifier: global.css sets `.hero h1 { margin: 0 }`, which
            // outranks a plain utility. `mb-3` escapes it only because that one comes from Bootstrap,
            // whose spacing utilities ship !important.
            className={clsx('!normal-case pb-0', subheading2First ? '!mb-8' : 'mb-3', {
                // Fluid type: long evergreen headlines were blowing past the layout at sm:6xl / xl:7xl
                '!font-semibold !text-[clamp(3rem,3vw_+_1rem,4.5rem)] !leading-[1.12] !text-pretty !text-black':
                    isEvergreen,
            })}
        >
            {mobileHeadingBreak && heading.includes('|') ? (
                <>
                    <span className="min-[1024px]:!hidden">
                        {heading.split('|')[0].trim()} |
                        <br />
                        {heading.split('|')[1]?.trim()}
                    </span>
                    <span className="hidden min-[1024px]:!inline">{heading}</span>
                </>
            ) : (
                heading
            )}
        </Typography>
    );

    const subheading2Block = subheading2 && (
        <Typography
            variant="h2-small"
            tag="p"
            color={isEvergreen ? 'Black' : 'Navy'}
            className={clsx('!normal-case !text-[1.75rem] !font-normal', subheading2First ? 'mb-2' : 'mb-8')}
        >
            {renderTextWithHighlight(subheading2, highlightWord)}
        </Typography>
    );

    const eyebrowBlock = eyebrow && (
        <Typography
            variant="h2-small"
            tag="p"
            color={isEvergreen ? 'Black' : 'Navy'}
            className="!normal-case !text-[1.75rem] !font-normal mb-2"
        >
            {eyebrow}
        </Typography>
    );

    return (
        <section
            className={clsx('hero w-full min-[1024px]:h-[700px] pb-0', {
                'bg-milan-grey-100': bgColor === 'bg-milan-grey-100',
            })}
            style={isHexColor ? { backgroundColor: bgColor } : undefined}
        >
            <div className="container h-full">
                <div className="flex flex-col min-[1024px]:flex-row items-center h-full">
                    {/* Text Content */}
                    <div className="min-[1024px]:w-3/4 text-center min-[1024px]:!text-left">
                        <div className="mt-4">
                            {subheading2First && subheading2Block}
                            {eyebrowBlock}
                        </div>
                        {headingBlock}
                        {subheading1Block}
                        {!subheading2First && subheading2Block}
                        {buttonText && (
                            <div className="flex justify-center min-[1024px]:justify-start">
                                <Button
                                    onClick={handleButtonClick}
                                    variant="primary"
                                    className="mb-6"
                                    style={{
                                        backgroundColor: '#000',
                                    }}
                                >
                                    {buttonText}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Image */}
                    <div className="w-full min-[1024px]:h-full relative">
                        <img
                            src={mobileImage}
                            alt={imageAlt || 'Hero image'}
                            className="min-[1024px]:!hidden w-full"
                            loading="eager"
                        />
                        <img
                            src={desktopImage}
                            alt={imageAlt || 'Hero image'}
                            className="hidden min-[1024px]:block min-[1024px]:absolute min-[1024px]:inset-0 min-[1024px]:w-full min-[1024px]:h-full xl:w-[120%] xl:-left-[10%] object-cover"
                            style={{ objectPosition: '93.5% 100%' }}
                            loading="eager"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default LandingPageHero;
